import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../environments/environment';

export type AnalyticsValue = string | number | boolean;
export type AnalyticsData = Record<string, AnalyticsValue>;

interface AnalyticsConfiguration {
  enabled: boolean;
  hostUrl: string | null;
  websiteId: string | null;
}

interface UmamiTracker {
  track(): void;
  track(event: string, data?: AnalyticsData): void;
}

declare global {
  interface Window {
    umami?: UmamiTracker;
  }
}

const MAX_PENDING_EVENTS = 200;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);
  private readonly pendingEvents: { name: string; data: AnalyticsData }[] = [];
  private initialized = false;
  private trackerReady = false;
  private assessmentFlowId: string | null = null;

  initialize(): void {
    if (!this.browser || this.initialized) return;
    this.initialized = true;
    void this.loadTracker();
  }

  track(name: string, data: AnalyticsData = {}): void {
    if (!this.browser || !this.validEventName(name)) return;
    const payload = this.sanitizeData({ ...data, ...this.flowData() });
    if (this.trackerReady && window.umami) {
      window.umami.track(name, payload);
      return;
    }
    if (this.pendingEvents.length < MAX_PENDING_EVENTS) {
      this.pendingEvents.push({ name, data: payload });
    }
  }

  beginAssessmentFlow(language: string): string {
    const flowId = this.newIdentifier();
    this.assessmentFlowId = flowId;
    this.track('iq_flow_started', { language });
    return flowId;
  }

  currentAssessmentFlowId(): string | null {
    return this.assessmentFlowId;
  }

  endAssessmentFlow(): void {
    this.assessmentFlowId = null;
  }

  createRequestTraceId(): string {
    return this.newIdentifier().replaceAll('-', '');
  }

  trackApiRequest(
    method: string,
    requestUrl: string,
    status: number,
    durationMs: number,
    traceId: string,
  ): void {
    this.track('api_request', {
      method: method.toUpperCase().slice(0, 10),
      route: this.routeTemplate(requestUrl),
      status,
      duration_ms: Math.max(0, Math.min(120_000, Math.round(durationMs))),
      trace_id: traceId,
      success: status >= 200 && status < 400,
    });
  }

  private async loadTracker(): Promise<void> {
    try {
      const response = await fetch(
        `${environment.apiBaseUrl}/api/v1/public-configuration/analytics`,
        {
          credentials: 'include',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        },
      );
      if (!response.ok) return;
      const configuration = (await response.json()) as AnalyticsConfiguration;
      if (!this.validConfiguration(configuration)) return;

      const script = this.document.createElement('script');
      script.defer = true;
      script.src = `${configuration.hostUrl}/script.js`;
      script.dataset['websiteId'] = configuration.websiteId;
      script.dataset['hostUrl'] = configuration.hostUrl;
      script.dataset['domains'] = window.location.hostname;
      script.dataset['doNotTrack'] = 'true';
      script.dataset['excludeSearch'] = 'true';
      script.dataset['excludeHash'] = 'true';
      script.dataset['performance'] = 'true';
      script.addEventListener('load', () => this.flushPendingEvents(), { once: true });
      this.document.head.append(script);
    } catch {
      // Analytics must never interrupt the assessment experience.
    }
  }

  private flushPendingEvents(): void {
    this.trackerReady = true;
    const tracker = window.umami;
    if (!tracker) return;
    for (const event of this.pendingEvents.splice(0)) tracker.track(event.name, event.data);
  }

  private validConfiguration(
    configuration: AnalyticsConfiguration,
  ): configuration is AnalyticsConfiguration & { hostUrl: string; websiteId: string } {
    if (!configuration.enabled || !configuration.hostUrl || !configuration.websiteId) return false;
    try {
      const host = new URL(configuration.hostUrl);
      return (
        (host.protocol === 'https:' || host.hostname === 'localhost') &&
        /^[0-9a-f]{8}-[0-9a-f-]{27}$/iu.test(configuration.websiteId)
      );
    } catch {
      return false;
    }
  }

  private flowData(): AnalyticsData {
    return this.assessmentFlowId ? { flow_id: this.assessmentFlowId } : {};
  }

  private newIdentifier(): string {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2).padEnd(20, '0')}`;
  }

  private validEventName(name: string): boolean {
    return /^[a-z][a-z0-9_]{0,49}$/u.test(name);
  }

  private sanitizeData(data: AnalyticsData): AnalyticsData {
    return Object.fromEntries(
      Object.entries(data)
        .slice(0, 50)
        .map(([key, value]) => [
          key.slice(0, 50),
          typeof value === 'string' ? value.slice(0, 500) : value,
        ]),
    );
  }

  private routeTemplate(url: string): string {
    const path = url.split(/[?#]/u)[0];
    return path
      .replace(/[0-9a-f]{8}-[0-9a-f-]{27}/giu, ':id')
      .replace(/\/questions\/\d+(?=\/|$)/gu, '/questions/:position')
      .slice(0, 200);
  }
}
